/* eslint-disable react-hooks/exhaustive-deps */
import CalendarIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../../providers/userContextProvider';

// Custom styled components
const DateRangePickerPaper = styled(Paper)(({ theme }) => ({
  width: '650px',
  padding: theme.spacing(2),
  borderRadius: '8px',
  overflow: 'hidden',
}));

const DateInputField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputAdornment-root': {
    marginRight: theme.spacing(1),
  },
}));

const MonthHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

interface DayButtonProps {
  isselected?: 'true' | 'false';
  istoday?: 'true' | 'false';
  isrange?: 'true' | 'false';
  israngestart?: 'true' | 'false';
  israngeend?: 'true' | 'false';
  ishovered?: 'true' | 'false';
  isdisabled?: 'true' | 'false';
}

const WeekRow = styled(Box)(() => ({
  display: 'flex',
  position: 'relative',
  width: '100%',
  height: '40px',
  marginBottom: '4px',
}));

interface DateCellProps {
  isInRange?: boolean;
  isFirstInRow?: boolean;
  isLastInRow?: boolean;
  isStartDate?: boolean;
  isEndDate?: boolean;
}

const DateCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isInRange'
    && prop !== 'isFirstInRow'
    && prop !== 'isLastInRow'
    && prop !== 'isStartDate'
    && prop !== 'isEndDate',
})<DateCellProps>(({
  theme,
  isInRange = false,
  isFirstInRow = false,
  isLastInRow = false,
  isStartDate = false,
  isEndDate = false,
}) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '40px',
  height: '40px',
  position: 'relative',
  zIndex: 0,

  ...(isInRange && {
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
      zIndex: -1,
      borderRadius: ((isFirstInRow || isStartDate) && (isLastInRow || isEndDate)) ? '20px'
        : (isFirstInRow || isStartDate) ? '20px 0 0 20px'
          : (isLastInRow || isEndDate) ? '0 20px 20px 0'
            : '0',
    },
  }),
}));

const DayButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isselected'
    && prop !== 'istoday'
    && prop !== 'isrange'
    && prop !== 'ishovered'
    && prop !== 'isdisabled',
})<DayButtonProps>(({
  theme,
  isselected = 'false',
  istoday = 'false',
  isrange = 'false',
  ishovered = 'false',
  isdisabled = 'false',
}) => ({
  minWidth: '36px',
  width: '36px',
  height: '36px',
  padding: 0,
  margin: '2px',
  borderRadius: '50%',
  fontWeight: isselected === 'true' ? 600 : 400,
  color: isdisabled === 'true' ? theme.palette.text.disabled
    : isselected === 'true' ? '#fff'
      : theme.palette.text.primary, // Change to black (text.primary)
  backgroundColor: isdisabled === 'true' ? 'transparent'
    : isselected === 'true' ? theme.palette.primary.main
      : 'transparent',
  border: istoday === 'true' && isselected !== 'true' ? `1px solid ${theme.palette.primary.main}` : 'none',
  position: 'relative',
  zIndex: 1,
  '&:hover': {
    backgroundColor: isdisabled === 'true' ? 'transparent'
      : isselected === 'true' ? theme.palette.primary.dark
        : `${theme.palette.primary.main}10`,
  },
}));

const WeekdayLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textAlign: 'center',
  fontWeight: 500,
  fontSize: '0.75rem',
  padding: theme.spacing(0.5),
  width: '40px',
}));

interface ActionButtonProps {
  color?: 'primary' | 'secondary';
}

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'color',
})<ActionButtonProps>(({ theme, color = 'secondary' }) => ({
  borderRadius: '40px',
  padding: theme.spacing(1, 3),
  textTransform: 'none',
  fontWeight: 500,
  backgroundColor: color === 'primary' ? theme.palette.primary.main : 'transparent',
  color: color === 'primary' ? theme.palette.common.white : theme.palette.primary.main,
  border: color === 'primary' ? 'none' : `1px solid ${theme.palette.primary.main}`,
  '&:hover': {
    backgroundColor: color === 'primary' ? theme.palette.primary.dark : theme.palette.action.hover,
  },
}));

export const DateRangePickerV2 = ({
  date1, date2, setDates, onClose,
}: {
  date1: Dayjs | null, date2: Dayjs | null, setDates: (d1: Dayjs, d2: Dayjs) => void, onClose?: () => void,
}) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [hoveredDay, setHoveredDay] = useState<Dayjs | null>(null);
  const [leftMonth, setLeftMonth] = useState<Dayjs>(dayjs());
  const [rightMonth, setRightMonth] = useState<Dayjs>(dayjs().add(1, 'month'));
  const [fromInputValue, setFromInputValue] = useState('');
  const [toInputValue, setToInputValue] = useState('');
  const [selectionMode, setSelectionMode] = useState<'start' | 'end' | 'complete'>('start');

  const { activeOrganization } = useContext(UserContext);
  const format = activeOrganization.applicableLocalization?.dateFormat ?? 'MM/DD/YYYY';

  // Initial setup
  useEffect(() => {
    if (date1) {
      setStartDate(date1);
      setFromInputValue(date1.format(format));
      setLeftMonth(date1.startOf('month'));
      setRightMonth(date1.add(1, 'month').startOf('month'));
    }
    if (date2) {
      setEndDate(date2);
      setToInputValue(date2.format(format));
      setSelectionMode('complete');
    }
  }, []);

  // Handle month navigation
  const handlePrevMonth = () => {
    setLeftMonth(leftMonth.subtract(1, 'month'));
    setRightMonth(rightMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setLeftMonth(leftMonth.add(1, 'month'));
    setRightMonth(rightMonth.add(1, 'month'));
  };

  // Handle date selection
  const handleDateClick = (day: Dayjs) => {
    if (selectionMode === 'start' || selectionMode === 'complete') {
      // Start a new selection
      setStartDate(day);
      setEndDate(null);
      setFromInputValue(day.format(format));
      setToInputValue('');
      setSelectionMode('end');
      setHoveredDay(null);
    } else if (selectionMode === 'end') {
      // Complete the selection
      if (startDate && day.isBefore(startDate)) {
        setEndDate(startDate);
        setStartDate(day);
        setFromInputValue(day.format(format));
        setToInputValue(startDate.format(format));
      } else {
        setEndDate(day);
        setToInputValue(day.format(format));
      }
      setSelectionMode('complete');
      setHoveredDay(null);
    }
  };

  // Handle mouse hover for range display
  const handleDayHover = (day: Dayjs) => {
    if (selectionMode === 'end') {
      setHoveredDay(day);
    }
  };

  const handleDayMouseLeave = () => {
    if (selectionMode === 'end') {
      setHoveredDay(null);
    }
  };

  // Generate array of all days for a month
  const generateDaysInMonth = (month: Dayjs) => {
    const firstDayOfMonth = month.startOf('month');
    const daysInMonth = month.daysInMonth();

    const days: Dayjs[] = [];

    // Add days in the month
    for (let i = 0; i < daysInMonth; i++) {
      days.push(firstDayOfMonth.add(i, 'day'));
    }

    return days;
  };

  // Check if a day is within the selected range
  const isDayInRange = (day: Dayjs) => {
    if (!startDate) return false;

    if (endDate) {
      return (
        (day.isAfter(startDate) || day.isSame(startDate, 'day'))
        && (day.isBefore(endDate) || day.isSame(endDate, 'day'))
      );
    }

    if (hoveredDay) {
      // Determine the correct range based on whether hoveredDay is before or after startDate
      const rangeStart = hoveredDay.isBefore(startDate) ? hoveredDay : startDate;
      const rangeEnd = hoveredDay.isBefore(startDate) ? startDate : hoveredDay;

      return (
        (day.isAfter(rangeStart) || day.isSame(rangeStart, 'day'))
        && (day.isBefore(rangeEnd) || day.isSame(rangeEnd, 'day'))
      );
    }

    return day.isSame(startDate, 'day');
  };

  // Handle submit
  const handleApply = () => {
    if (startDate && endDate) {
      setDates(startDate, endDate);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setStartDate(date1);
    setEndDate(date2);
    setFromInputValue(date1 ? date1.format(format) : '');
    setToInputValue(date2 ? date2.format(format) : '');
    if (date1 && date2) {
      setSelectionMode('complete');
    } else {
      setSelectionMode('start');
    }
    if (date1) {
      setLeftMonth(date1.startOf('month'));
      setRightMonth(date1.add(1, 'month').startOf('month'));
    }
  };

  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const leftMonthDays = generateDaysInMonth(leftMonth);
  const rightMonthDays = generateDaysInMonth(rightMonth);

  const minDate = dayjs('2020-01-02');
  const maxDate = dayjs();

  const isDisabled = (day: Dayjs) => day.isAfter(maxDate) || day.isBefore(minDate);

  // Group days into weeks
  const groupDaysIntoWeeks = (days: Dayjs[]) => {
    // Calculate offset for the 1st day of the month
    const firstDay = days[0];
    const firstDayOffset = firstDay.day(); // 0 for Sunday, 1 for Monday, etc.

    // Create weeks array
    const weeks: (Dayjs | null)[][] = [];
    let currentWeek: (Dayjs | null)[] = Array(firstDayOffset).fill(null);

    days.forEach((day) => {
      currentWeek.push(day);

      // If Saturday (day 6) or last day of month, start a new week
      if (day.day() === 6 || day.isSame(days[days.length - 1], 'day')) {
        // If the last week isn't full, pad it
        while (currentWeek.length < 7) {
          currentWeek.push(null);
        }

        weeks.push(currentWeek);
        currentWeek = [];
      }
    });

    return weeks;
  };

  // Render calendar
  const renderCalendar = (days: Dayjs[], monthLabel: string, month: Dayjs) => {
    const weeks = groupDaysIntoWeeks(days);

    return (
      <Box>
        {monthLabel && (
          <MonthHeader>
            <Typography variant="h6">{monthLabel}</Typography>
          </MonthHeader>
        )}

        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {weekdays.map((day, i) => (
              <WeekdayLabel key={`weekday-${i}`}>{day}</WeekdayLabel>
            ))}
          </Box>

          {weeks.map((week, weekIndex) => (
            <WeekRow key={`week-${weekIndex}`}>
              {week.map((day, dayIndex) => {
                if (day === null) {
                  return <Box key={`empty-${dayIndex}`} width="40px" />;
                }

                const isToday = day.isSame(dayjs(), 'day');
                const isStartDate = startDate?.isSame(day, 'day');
                const isEndDate = endDate?.isSame(day, 'day');
                const isSelected = isStartDate || isEndDate;
                const isInRange = isDayInRange(day);

                // Find first and last days in range for this row
                const actualDaysInRow = week.filter((d) => d !== null) as Dayjs[];
                const daysInRange = actualDaysInRow.filter((d) => isDayInRange(d));

                const isFirstInRow = daysInRange.length > 0 && day.isSame(daysInRange[0], 'day');
                const isLastInRow = daysInRange.length > 0 && day.isSame(daysInRange[daysInRange.length - 1], 'day');

                const disabled = isDisabled(day);

                return (
                  <DateCell
                    key={`cell-${dayIndex}`}
                    isInRange={isInRange}
                    isFirstInRow={isFirstInRow}
                    isLastInRow={isLastInRow}
                    isStartDate={isStartDate}
                    isEndDate={isEndDate}
                  >
                    <DayButton
                      key={`day-${dayIndex}`}
                      istoday={isToday ? 'true' : 'false'}
                      isselected={isSelected ? 'true' : 'false'}
                      isrange={'false'}
                      ishovered={'false'}
                      isdisabled={disabled ? 'true' : 'false'}
                      onClick={() => !disabled && handleDateClick(day)}
                      onMouseEnter={() => !disabled && handleDayHover(day)}
                      onMouseLeave={handleDayMouseLeave}
                      disabled={disabled}
                      disableRipple
                    >
                      {day.date()}
                    </DayButton>
                  </DateCell>
                );
              })}
            </WeekRow>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangePickerPaper elevation={3}>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>From</Typography>
              <DateInputField
                value={fromInputValue}
                placeholder="DD/MM/YYYY"
                InputProps={{
                  readOnly: true,
                  startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>To</Typography>
              <DateInputField
                value={toInputValue}
                placeholder="DD/MM/YYYY"
                InputProps={{
                  readOnly: true,
                  startAdornment: <CalendarIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2,
            mb: 1,
          }}>
            <Button
              onClick={handlePrevMonth}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <ChevronLeftIcon />
            </Button>

            <Grid container>
              <Grid item xs={6}>
                <MonthHeader>
                  <Typography variant="h6">{leftMonth.format('MMMM YYYY')}</Typography>
                </MonthHeader>
              </Grid>
              <Grid item xs={6}>
                <MonthHeader>
                  <Typography variant="h6">{rightMonth.format('MMMM YYYY')}</Typography>
                </MonthHeader>
              </Grid>
            </Grid>

            <Button
              onClick={handleNextMonth}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <ChevronRightIcon />
            </Button>
          </Box>

          <Grid container>
            <Grid item xs={6}>
              {renderCalendar(leftMonthDays, '', leftMonth)}
            </Grid>
            <Grid item xs={6}>
              {renderCalendar(rightMonthDays, '', rightMonth)}
            </Grid>
          </Grid>

          <Box sx={{
            display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2,
          }}>
            <ActionButton color="secondary" onClick={handleCancel}>
              Cancel
            </ActionButton>
            <ActionButton
              color="primary"
              onClick={handleApply}
              disabled={!startDate || !endDate}
            >
              Apply
            </ActionButton>
          </Box>
        </Box>
      </DateRangePickerPaper>
    </LocalizationProvider>
  );
};
